import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { Button, CallAdminIcon, Preloader } from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext } from 'localization';
import { isSandbox } from 'sandbox';
import notificationService from 'services/notificationService';

import s from './Calls.module.scss';
import {
  CALL_DONE,
  CALL_FRAGMENT,
  CALLS,
  ICallDoneRequest,
  ICallDoneResponse,
  CallsResponse,
} from './schema';

export const Calls: FC = () => {
  const { t } = useContext(LocalizationContext);
  const [isVisible, setIsVisible] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible) return;

    const handleMouseDown = (e: MouseEvent): void => {
      const target = e.target as Node;

      if (
        menuRef.current?.contains(target) ||
        openerRef.current?.contains(target)
      ) {
        return;
      }

      setIsVisible(false);
    };

    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setIsVisible(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isVisible]);

  const [previousCallsByHostId, setPreviousCallsByHostId] = useState<Record<
    number,
    boolean
  > | null>(null);

  const { data, loading } = useQuery<CallsResponse>(CALLS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
    notifyOnNetworkStatusChange: true,
  });

  const [callDone] = useMutation<ICallDoneResponse, ICallDoneRequest>(
    CALL_DONE,
    {
      onError: () =>
        notificationService.info({
          t,
          type: 'warning',
          title: t('calls.confirm.error'),
        }),
      update: (cache, { data }, { variables }) => {
        if (!data || !variables) return;

        if (!data.adminCallDone) {
          return notificationService.info({
            t,
            type: 'warning',
            title: t('calls.confirm.error'),
          });
        }

        cache.writeFragment({
          id: cache.identify({ __typename: 'HostOverview', id: variables.id }),
          data: { admin_called_at: null },
          fragment: CALL_FRAGMENT,
        });
      },
      optimisticResponse: () => ({
        adminCallDone: true,
      }),
    }
  );

  useEffect(() => {
    if (!data) return;

    if (!previousCallsByHostId) {
      setPreviousCallsByHostId(
        data.hostsOverview.reduce<Record<number, boolean>>((acc, host) => {
          if (host.admin_called_at) {
            acc[host.id] = true;
          }
          return acc;
        }, {})
      );

      return;
    }

    let hasNewCall = false;

    data.hostsOverview.forEach((host) => {
      if (host.admin_called_at && !previousCallsByHostId[host.id]) {
        hasNewCall = true;
      }
    });

    if (hasNewCall && !isSandbox) {
      const audio = new Audio(
        'http://static.smartshell.gg/fe_sounds/fe_admin_called.mp3'
      );
      audio.autoplay = true;
    }

    setPreviousCallsByHostId(
      data.hostsOverview.reduce<Record<number, boolean>>((acc, host) => {
        if (host.admin_called_at) {
          acc[host.id] = true;
        }
        return acc;
      }, {})
    );
  }, [data]);

  const calls =
    data?.hostsOverview.filter((host) => host.admin_called_at) ?? [];
  const groupTitleById = useMemo<Record<number, string>>(
    () =>
      data?.hostGroupsOverview.reduce<Record<number, string>>((acc, group) => {
        acc[group.id] = group.title;

        return acc;
      }, {}) ?? {},
    [data?.hostGroupsOverview]
  );

  return (
    <div className={s.container}>
      <div ref={openerRef} className={s.button_block}>
        <Button
          className={cn(
            s.callsButton,
            Boolean(calls.length) && s.callsButton_active
          )}
          variant="ghost"
          onClick={() => {
            setIsVisible((prev) => !prev);
          }}
        >
          <CallAdminIcon size="m" />
          <span>{t('calls')}</span>
        </Button>
      </div>

      {Boolean(calls.length) && <div className={s.counter}>{calls.length}</div>}
      {isVisible && (
        <div className={s.menu} ref={menuRef}>
          <Preloader
            loading={!data && loading}
            hasData={Boolean(calls.length)}
            noDataPlaceholder={t('calls.placeholder')}
            className={s.placeholderContainer}
          >
            {calls.map((host) => (
              <div key={`adminCalls-${host.id}`} className={s.host}>
                <div className={s.info}>
                  {host.alias}
                  <span className={s.group}>
                    {groupTitleById[host.group_id]}
                  </span>
                </div>
                <Button
                  size="s"
                  onClick={() => callDone({ variables: { id: host.id } })}
                >
                  {t('confirm')}
                </Button>
              </div>
            ))}
          </Preloader>
        </div>
      )}
    </div>
  );
};
